import React, { useState } from "react";
import { api, ApiError, resolveUrl, type Property } from "../../api";
import { useToast } from "../../ToastContext";
import { fileToResizedDataUrl } from "../../photoUtils";
import { PhotoPickerButtons } from "./PhotoPickerButtons";
import { PhotoLightbox } from "../shared/PhotoLightbox";
import { useLanguage } from "../../i18n";

export function PropertyPhotos({ property, onChanged }: { property: Property; onChanged: () => void }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const toast = useToast();
  const { t } = useLanguage();

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError("");
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) continue;
        const dataUrl = await fileToResizedDataUrl(file);
        await api.post(`/api/properties/${property.id}/photos`, { dataUrl });
      }
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : t("propertyPhotos.uploadFailed"));
    } finally {
      setUploading(false);
    }
  }

  async function removePhoto(url: string) {
    try {
      await api.del(`/api/properties/${property.id}/photos`, { url });
      toast(t("propertyPhotos.photoRemoved"));
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("propertyPhotos.couldNotRemove"));
    }
  }

  return (
    <div style={{ marginBottom: 14 }}>
      {error && <div className="error-banner">{error}</div>}
      <div className="photo-grid">
        {property.photos.map((url, i) => (
          <div className="photo-thumb" key={url}>
            <img src={resolveUrl(url)} alt="" onClick={() => setLightboxIndex(i)} style={{ cursor: "pointer" }} />
            <button
              type="button"
              className="photo-thumb-remove"
              onClick={() => removePhoto(url)}
              aria-label={t("propertyPhotos.removePhotoAria")}
              title={t("propertyPhotos.removePhotoAria")}
            >
              &times;
            </button>
          </div>
        ))}
        <PhotoPickerButtons busy={uploading} onFiles={handleFiles} />
      </div>
      {lightboxIndex !== null && (
        <PhotoLightbox photos={property.photos} index={lightboxIndex} onClose={() => setLightboxIndex(null)} />
      )}
    </div>
  );
}
